import React, { useEffect } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Alert, Button, Container } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { getLoginCookieAndSession } from "../../utils";
import { useStripeAccountStatus } from "../../hooks/useStripeAccountStatus";
import { CenteredLoading } from "../../components/common/CenteredLoading";
import { fetchCategories } from "../../redux/actions/categoryAction";
import type { RootState } from "../../redux/store";
import SellingWizard from "./selling/SellingWizard";
import SellingErrorBoundary from "./selling/SellingErrorBoundary";

const Sell: React.FC = () => {
  const navigate = useNavigate();
  const profileId = getLoginCookieAndSession();
  const {
    loading: checkingSeller,
    isApproved,
    sellRoute,
    error: sellerStatusError,
    retry: retrySellerStatus,
  } = useStripeAccountStatus();
  const dispatch = useDispatch();
  const { categoriesData, loading, error } = useSelector(
    (state: RootState) => state.categories,
  );
  const staging =
    process.env.REACT_APP_FIREBASE_PROJECT_ID === "fp-staging-9026f";

  useEffect(() => {
    if (!profileId) {
      navigate("/", { replace: true });
    }
  }, [profileId, navigate]);

  useEffect(() => {
    if (
      profileId &&
      isApproved &&
      staging &&
      !categoriesData.categories.length &&
      !loading &&
      !error
    ) {
      dispatch(fetchCategories());
    }
  }, [
    profileId,
    isApproved,
    staging,
    categoriesData.categories.length,
    loading,
    error,
    dispatch,
  ]);

  if (!profileId) return null;

  if (checkingSeller) return <CenteredLoading />;
  if (sellerStatusError)
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" onClick={retrySellerStatus}>
              Retry
            </Button>
          }
        >
          {sellerStatusError}
        </Alert>
      </Container>
    );
  if (!isApproved) return <Navigate to={sellRoute} replace />;
  if (!staging)
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="info">
          Listing creation is not enabled in this environment.
        </Alert>
      </Container>
    );
  return (
    <SellingErrorBoundary key={profileId}>
      <SellingWizard
        accountId={String(profileId)}
        categories={categoriesData.categories.filter(
          (category) => category.source === "catalogue",
        )}
        categoriesLoading={loading}
        categoriesError={error}
        retryCategories={() => dispatch(fetchCategories(true))}
      />
    </SellingErrorBoundary>
  );
};

export default Sell;
